import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Printer } from "lucide-react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError, rupiah } from "./lib/format.js";

const MONTHS = [
  "Januari","Februari","Maret","April","Mei","Juni",
  "Juli","Agustus","September","Oktober","November","Desember",
];

const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

export function PayrollPage({ identity }) {
  const now = new Date();
  const [month, setMonth]         = useState(now.getMonth() + 1);
  const [year, setYear]           = useState(now.getFullYear());
  const [staff, setStaff]         = useState([]);
  const [rows, setRows]           = useState([]);
  const [isCalculated, setIsCalculated] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [expanded, setExpanded]   = useState({});
  const [details, setDetails]     = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const canManagePayroll = Boolean(
    identity?.appUser?.is_platform_admin
      || identity?.permissions?.has?.("payroll.write")
  );
  const personalOnly = Boolean(
    identity
      && !canManagePayroll
      && /dokter|nakes|terapis|perawat|bidan|akupunkt|dental|gigi|fisioterapi|apoteker|farmasi|wellness|estetik/i.test(identity.role?.role_name || "")
  );

  useEffect(() => {
    loadPeriod();
  }, [month, year, personalOnly, identity?.appUser?.id]); // eslint-disable-line

  function periodBounds() {
    const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
    const nextMonth = month === 12
      ? `${year + 1}-01-01`
      : `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const endDate = new Date(new Date(nextMonth).getTime() - 86400000)
      .toISOString().slice(0, 10);
    return { startDate, endDate };
  }

  async function loadPeriod() {
    setLoading(true);
    setError("");
    setIsCalculated(false);
    setExpanded({});
    setDetails({});
    try {
      if (personalOnly) {
        const { startDate, endDate } = periodBounds();
        const { data: preview, error: previewError } = await supabase.rpc("preview_my_thp", {
          p_clinic_id: CLINIC_ID,
          p_period_start: startDate,
          p_period_end: endDate,
        });
        if (previewError) throw previewError;
        if (!preview?.has_setting) {
          setError("Konfigurasi THP akun Anda belum dibuat. Hubungi Administrator Klinik.");
          setRows([]);
          return;
        }
        setRows([{
          ...preview,
          nakes_id: preview.user_id,
          name: preview.name,
          payroll_type: preview.thp_type,
          base_salary: preview.monthly_salary,
          attendance_allowance: preview.attendance_allowance,
          total_commission: preview.total_commission,
          take_home_pay: preview.take_home_pay,
          attendance_days: preview.attendance_days,
          _preview: true,
        }]);
        setIsCalculated(true);
        return;
      }

      const [staffRes, payrollRes] = await Promise.all([
        supabase
          .from("staff")
          .select("id,user_id,name,division,competencies,base_salary,allowance_per_attendance,default_commission,payroll_type,profit_share_rate")
          .eq("clinic_id", CLINIC_ID)
          .eq("status", "ACTIVE")
          .order("name"),
        supabase
          .from("payroll")
          .select("*")
          .eq("clinic_id", CLINIC_ID)
          .eq("period_month", month)
          .eq("period_year", year),
      ]);
      if (staffRes.error) throw staffRes.error;
      if (payrollRes.error) throw payrollRes.error;

      const staffData   = staffRes.data   || [];
      const payrollData = payrollRes.data || [];

      setStaff(staffData);

      if (payrollData.length > 0) {
        // Tampilkan data tersimpan
        setRows(
          payrollData.map((p) => {
            const staffMember = staffData.find((s) => s.id === p.nakes_id || s.user_id === p.nakes_id);
            return {
              ...p,
              user_id: p.user_id || staffMember?.user_id || p.nakes_id,
              name: staffMember?.name || p.nakes_id,
              payroll_type: staffMember?.payroll_type || "—",
              _saved: true,
            };
          })
        );
        setIsCalculated(true);
      } else {
        setRows([]);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleCalculate() {
    setExpanded({});
    setDetails({});
    if (personalOnly) {
      await loadPeriod();
      return;
    }
    if (!staff.length) { setError("Belum ada akun yang memiliki konfigurasi THP aktif."); return; }
    setCalculating(true);
    setError("");
    try {
      const { startDate, endDate } = periodBounds();

      const { data: run, error: runError } = await supabase.rpc("process_thp_monthly", {
        p_clinic_id: CLINIC_ID,
        p_period_start: startDate,
        p_period_end: endDate,
      });
      if (runError) throw runError;
      const { data: itemData, error: itemError } = await supabase
        .from("thp_monthly_run_items")
        .select("*")
        .eq("clinic_id", CLINIC_ID)
        .eq("run_id", run.run_id)
        .order("name_snapshot");
      if (itemError) throw itemError;
      setRows((itemData || []).map((item) => ({
        ...item,
        nakes_id: item.user_id,
        name: item.name_snapshot,
        payroll_type: item.thp_type_snapshot,
        base_salary: item.salary_snapshot,
        attendance_allowance: item.attendance_allowance_snapshot,
        total_commission: item.total_commission,
        take_home_pay: item.take_home_pay,
        attendance_days: item.attendance_days,
        _saved: true,
      })));
      setIsCalculated(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setCalculating(false);
    }
  }

  async function handleSave() {
    if (personalOnly || !rows.length) return;
    setSaving(true);
    setError("");
    try {
      const records = rows.map((r) => ({
        id:                   `PAY-${r.nakes_id}-${year}${String(month).padStart(2, "0")}`,
        clinic_id:            CLINIC_ID,
        nakes_id:             r.nakes_id,
        period_month:         month,
        period_year:          year,
        base_salary:          r.base_salary,
        attendance_days:      r.attendance_days,
        attendance_allowance: r.attendance_allowance,
        total_commission:     r.total_commission,
        take_home_pay:        r.take_home_pay,
      }));

      const { error: e } = await supabase
        .from("payroll")
        .upsert(records, { onConflict: "id" });
      if (e) throw e;

      await loadPeriod();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  function downloadSummary() {
    if (!rows.length) return;
    const header = ["Nama", "Tipe THP", "Gaji Bulanan", "Hari Hadir", "Tunjangan Kehadiran", "Komisi", "Take Home Pay"];
    const lines = rows.map((row) => [
      row.name,
      row.payroll_type,
      row.base_salary,
      row.attendance_days,
      row.attendance_allowance,
      row.total_commission,
      row.take_home_pay,
    ].map((value) => `"${String(value ?? '').replaceAll('"', '""')}"`).join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rekap-thp-${year}-${String(month).padStart(2, '0')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function rowKey(row) {
    return row.user_id || row.nakes_id;
  }

  async function loadDetail(row) {
    const key = rowKey(row);
    if (!key) return;
    if (details[key]) {
      setExpanded((current) => ({ ...current, [key]: true }));
      return;
    }

    setDetailLoading((current) => ({ ...current, [key]: true }));
    setError("");
    try {
      const { startDate, endDate } = periodBounds();
      const { data, error: detailError } = await supabase.rpc("preview_thp_detail", {
        p_clinic_id: CLINIC_ID,
        p_user_id: key,
        p_period_start: startDate,
        p_period_end: endDate,
      });
      if (detailError) throw detailError;
      setDetails((current) => ({ ...current, [key]: data || {} }));
      setExpanded((current) => ({ ...current, [key]: true }));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setDetailLoading((current) => ({ ...current, [key]: false }));
    }
  }

  function toggleDetail(row) {
    const key = rowKey(row);
    if (!key) return;
    if (expanded[key]) {
      setExpanded((current) => ({ ...current, [key]: false }));
      return;
    }
    loadDetail(row);
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function printDetail(row, detail) {
    const popup = window.open("", "_blank", "width=1100,height=800");
    if (!popup) {
      setError("Jendela cetak diblokir browser. Izinkan pop-up lalu coba lagi.");
      return;
    }

    const attendance = detail?.attendance || [];
    const commissions = detail?.commissions || [];
    const marketingCommissions = detail?.marketing_commissions || [];
    const totals = detail?.totals || {};
    const period = `${detail?.period_start || periodBounds().startDate.slice(0, 7)} sampai ${detail?.period_end || periodBounds().endDate}`;
    const money = (value) => rupiah.format(Number(value || 0));
    const attendanceRows = attendance.length
      ? attendance.map((item) => `<tr><td>${escapeHtml(item.attendance_date)}</td><td>${escapeHtml(item.source || "Tindakan")}</td><td class="amount">${money(item.allowance_amount)}</td></tr>`).join("")
      : `<tr><td colspan="3" class="muted">Belum ada rincian kehadiran.</td></tr>`;
    const commissionRows = commissions.length
      ? commissions.map((item) => `<tr><td>${escapeHtml(item.transaction_date)}</td><td>${escapeHtml(item.visit_number || item.visit_id)}</td><td>${escapeHtml(item.client_name)}</td><td>${escapeHtml(item.service_name)}</td><td class="amount">${money(item.commission_amount)}</td></tr>`).join("")
      : `<tr><td colspan="5" class="muted">Belum ada komisi tindakan.</td></tr>`;
    const marketingRows = marketingCommissions.map((item) => `<tr><td>${escapeHtml(item.transaction_date)}</td><td>${escapeHtml(item.visit_number || item.visit_id)}</td><td>${escapeHtml(item.client_name)}</td><td class="amount">${money(item.commission_amount)}</td></tr>`).join("");

    popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Rincian THP - ${escapeHtml(detail?.name || row.name)}</title><style>
      *{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#142033;margin:16px;font-size:14px}h1{font-size:20px;margin:0 0 12px}h2{font-size:16px;margin:18px 0 8px}.meta{border-top:3px solid #2563eb;border-bottom:1px solid #cbd5e1;padding:8px 0;display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px}.meta b{display:block;margin-top:5px}.report{width:100%;border-collapse:collapse}.report th{background:#eff6ff;text-align:left;border-bottom:1px solid #cbd5e1;padding:8px}.report td{border-bottom:1px solid #dbe3ee;padding:8px}.amount{text-align:right;white-space:nowrap}.muted{color:#64748b}.totals{border-top:3px solid #2563eb;margin-top:12px;padding-top:8px;max-width:520px;margin-left:auto}.total-row{display:flex;justify-content:space-between;padding:4px 0}.grand{font-size:18px;font-weight:700;margin-top:10px;padding-top:10px;border-top:1px solid #94a3b8}@media print{body{margin:8mm}button{display:none}}
    </style></head><body>
      <div class="meta"><div>Nakes<b>${escapeHtml(detail?.name || row.name)}</b></div><div>Periode<b>${escapeHtml(period)}</b></div><div>Gaji Pokok<b>${money(detail?.monthly_salary ?? row.base_salary)}</b></div></div>
      <h2>Rincian Kehadiran (${attendance.length} hari)</h2><table class="report"><thead><tr><th>Tanggal</th><th>Sumber</th><th class="amount">Tunjangan</th></tr></thead><tbody>${attendanceRows}</tbody></table>
      <h2>Rincian Komisi Tindakan (${commissions.length} tindakan)</h2><table class="report"><thead><tr><th>Tanggal</th><th>No Visit</th><th>Klien</th><th>Tindakan</th><th class="amount">Komisi</th></tr></thead><tbody>${commissionRows}</tbody></table>
      ${marketingCommissions.length ? `<h2>Rincian Komisi Marketing (${marketingCommissions.length} transaksi)</h2><table class="report"><thead><tr><th>Tanggal</th><th>No Visit</th><th>Klien</th><th class="amount">Komisi</th></tr></thead><tbody>${marketingRows}</tbody></table>` : ""}
      <div class="totals"><div class="total-row"><span>Gaji Pokok</span><b>${money(detail?.monthly_salary ?? row.base_salary)}</b></div><div class="total-row"><span>Tunjangan Kehadiran (${attendance.length} hari)</span><b>${money(totals.attendance_allowance)}</b></div><div class="total-row"><span>Komisi Tindakan (${commissions.length} tindakan)</span><b>${money(totals.action_commission)}</b></div>${marketingCommissions.length ? `<div class="total-row"><span>Komisi Marketing (${marketingCommissions.length} transaksi)</span><b>${money(totals.marketing_commission)}</b></div>` : ""}<div class="total-row grand"><span>Total Take Home Pay</span><b>${money(totals.take_home_pay ?? row.take_home_pay)}</b></div></div>
    </body></html>`);
    popup.document.close();
    popup.focus();
    setTimeout(() => popup.print(), 250);
  }

  const totalTHP  = rows.reduce((s, r) => s + Number(r.take_home_pay        || 0), 0);
  const totalAtt  = rows.reduce((s, r) => s + Number(r.attendance_allowance  || 0), 0);
  const totalComm = rows.reduce((s, r) => s + Number(r.total_commission      || 0), 0);
  const allSaved  = !personalOnly && rows.length > 0 && rows.every((r) => r._saved);

  return (
    <div className="page-stack">

      {/* ── Header & Period Picker ── */}
      <article className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">Manajemen SDM XV</span>
            <h3>Penggajian (Payroll)</h3>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <strong>Terjadi kesalahan.</strong> {error}
          </div>
        )}

        <div className="pay-toolbar">
          <div className="pay-period">
            <label>Periode</label>
            <div className="pay-period-selects">
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="pay-actions">
            {personalOnly ? (
              <>
                <button className="button button-secondary" onClick={handleCalculate} disabled={loading}>
                  {loading ? "Memuat…" : "Muat Ulang THP Saya"}
                </button>
                {isCalculated && <button className="button button-quiet" onClick={downloadSummary}>Unduh Ringkasan</button>}
              </>
            ) : (
              <>
                <button className="button button-secondary" onClick={handleCalculate} disabled={calculating || loading}>
                  {calculating ? "Memproses…" : "Hitung THP"}
                </button>
                {isCalculated && !allSaved && (
                  <button className="button button-primary" onClick={handleSave} disabled={saving}>
                    {saving ? "Menyimpan…" : "Simpan Payroll"}
                  </button>
                )}
                {allSaved && (
                  <>
                    <button className="button button-secondary" onClick={handleCalculate} disabled={calculating}>Hitung Ulang</button>
                    <button className="button button-quiet" onClick={downloadSummary}>Unduh Rekap</button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {personalOnly && isCalculated && (
          <div className="pay-saved-badge" style={{ background: "#eff6ff", borderColor: "#bfdbfe", color: "#1d4ed8" }}>
            Estimasi THP pribadi untuk {MONTHS[month - 1]} {year}. Hanya transaksi lunas yang dihitung.
          </div>
        )}
        {allSaved && (
          <div className="pay-saved-badge">
            ✓ Payroll {MONTHS[month - 1]} {year} sudah tersimpan
          </div>
        )}
      </article>

      {/* ── Tabel Payroll ── */}
      {(isCalculated || loading) && (
        <article className="panel table-panel">
          {loading ? (
            <div className="content-loading"><div className="loader" /><span>Memuat data…</span></div>
          ) : (
            <>
              <table className="mt-table">
                <thead>
                  <tr>
                    <th>NAKES</th>
                    <th style={{ width: 84 }}>RINCIAN</th>
                    <th>TIPE THP</th>
                    <th style={{ textAlign: "right" }}>GAJI POKOK</th>
                    <th style={{ textAlign: "center" }}>HADIR</th>
                    <th style={{ textAlign: "right" }}>TUNJANGAN</th>
                    <th style={{ textAlign: "right" }}>KOMISI</th>
                    <th style={{ textAlign: "right" }}>TAKE HOME PAY</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <Fragment key={row.nakes_id}>
                      <tr key={row.nakes_id}>
                        <td><strong>{row.name}</strong></td>
                        <td>
                          <button className="pay-detail-button" type="button" onClick={() => toggleDetail(row)} disabled={detailLoading[rowKey(row)]}>
                            {expanded[rowKey(row)] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            {detailLoading[rowKey(row)] ? "Memuat…" : "Rincian"}
                          </button>
                        </td>
                        <td>
                          <span className="status status-neutral">{row.payroll_type || "Standar"}</span>
                        </td>
                        <td style={{ textAlign: "right" }}>{rupiah.format(Number(row.base_salary))}</td>
                        <td style={{ textAlign: "center" }}>
                          <span className="status status-positive">{row.attendance_days} hari</span>
                        </td>
                        <td style={{ textAlign: "right" }}>{rupiah.format(Number(row.attendance_allowance))}</td>
                        <td style={{ textAlign: "right" }}>{rupiah.format(Number(row.total_commission))}</td>
                        <td style={{ textAlign: "right" }}>
                          <strong style={{ color: "var(--green-700, #2f7d62)" }}>
                            {rupiah.format(Number(row.take_home_pay))}
                          </strong>
                        </td>
                      </tr>
                      {expanded[rowKey(row)] ? (
                        <tr key={`${row.nakes_id}-detail`} className="pay-detail-row">
                          <td colSpan={8}>
                            {details[rowKey(row)] ? (() => {
                              const detail = details[rowKey(row)];
                              const attendance = detail.attendance || [];
                              const commissions = detail.commissions || [];
                              const marketingCommissions = detail.marketing_commissions || [];
                              const totals = detail.totals || {};
                              return (
                                <div className="pay-report">
                                  <div className="pay-report-head">
                                    <div>
                                      <span className="eyebrow">Rincian THP</span>
                                      <h3>{detail.name || row.name}</h3>
                                    </div>
                                    <button className="button button-quiet" type="button" onClick={() => printDetail(row, detail)}>
                                      <Printer size={15} /> Cetak
                                    </button>
                                  </div>
                                  <div className="pay-report-meta">
                                    <span>Nakes <strong>{detail.name || row.name}</strong></span>
                                    <span>Periode <strong>{detail.period_start || `${year}-${String(month).padStart(2, "0")}-01`} s.d. {detail.period_end || periodBounds().endDate}</strong></span>
                                    <span>Gaji Pokok <strong>{rupiah.format(Number(detail.monthly_salary ?? row.base_salary ?? 0))}</strong></span>
                                  </div>

                                  <h4>Rincian Kehadiran ({attendance.length} hari)</h4>
                                  <table className="pay-report-table">
                                    <thead><tr><th>Tanggal</th><th>Sumber</th><th style={{ textAlign: "right" }}>Tunjangan</th></tr></thead>
                                    <tbody>
                                      {attendance.length ? attendance.map((item, index) => (
                                        <tr key={`${item.attendance_date}-${index}`}><td>{item.attendance_date}</td><td>{item.source || "Tindakan"}</td><td style={{ textAlign: "right" }}>{rupiah.format(Number(item.allowance_amount || 0))}</td></tr>
                                      )) : <tr><td colSpan={3} className="pay-report-empty">Belum ada rincian kehadiran.</td></tr>}
                                    </tbody>
                                  </table>

                                  <h4>Rincian Komisi Tindakan ({commissions.length} tindakan)</h4>
                                  <table className="pay-report-table">
                                    <thead><tr><th>Tanggal</th><th>No Visit</th><th>Klien</th><th>Tindakan</th><th style={{ textAlign: "right" }}>Komisi</th></tr></thead>
                                    <tbody>
                                      {commissions.length ? commissions.map((item, index) => (
                                        <tr key={`${item.transaction_id}-${item.visit_id}-${index}`}><td>{item.transaction_date}</td><td>{item.visit_number || item.visit_id || "—"}</td><td>{item.client_name || "—"}</td><td>{item.service_name || "Tindakan"}</td><td style={{ textAlign: "right" }}>{rupiah.format(Number(item.commission_amount || 0))}</td></tr>
                                      )) : <tr><td colSpan={5} className="pay-report-empty">Belum ada komisi tindakan.</td></tr>}
                                    </tbody>
                                  </table>

                                  {marketingCommissions.length ? <>
                                    <h4>Rincian Komisi Marketing ({marketingCommissions.length} transaksi)</h4>
                                    <table className="pay-report-table">
                                      <thead><tr><th>Tanggal</th><th>No Visit</th><th>Klien</th><th style={{ textAlign: "right" }}>Komisi</th></tr></thead>
                                      <tbody>{marketingCommissions.map((item, index) => <tr key={`${item.transaction_id}-${item.visit_id}-${index}`}><td>{item.transaction_date}</td><td>{item.visit_number || item.visit_id || "—"}</td><td>{item.client_name || "—"}</td><td style={{ textAlign: "right" }}>{rupiah.format(Number(item.commission_amount || 0))}</td></tr>)}</tbody>
                                    </table>
                                  </> : null}

                                  <div className="pay-report-totals">
                                    <div><span>Gaji Pokok</span><strong>{rupiah.format(Number(detail.monthly_salary ?? row.base_salary ?? 0))}</strong></div>
                                    <div><span>Tunjangan Kehadiran ({attendance.length} hari)</span><strong>{rupiah.format(Number(totals.attendance_allowance || 0))}</strong></div>
                                    <div><span>Komisi Tindakan ({commissions.length} tindakan)</span><strong>{rupiah.format(Number(totals.action_commission || 0))}</strong></div>
                                    {marketingCommissions.length ? <div><span>Komisi Marketing ({marketingCommissions.length} transaksi)</span><strong>{rupiah.format(Number(totals.marketing_commission || 0))}</strong></div> : null}
                                    <div className="pay-report-grand"><span>Total Take Home Pay</span><strong>{rupiah.format(Number(totals.take_home_pay ?? row.take_home_pay ?? 0))}</strong></div>
                                  </div>
                                </div>
                              );
                            })() : <div className="pay-report-loading">Memuat rincian THP…</div>}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid #e8e2d5", background: "#faf8f3" }}>
                    <td colSpan={3}><strong>TOTAL</strong></td>
                    <td />
                    <td />
                    <td style={{ textAlign: "right" }}><strong>{rupiah.format(totalAtt)}</strong></td>
                    <td style={{ textAlign: "right" }}><strong>{rupiah.format(totalComm)}</strong></td>
                    <td style={{ textAlign: "right" }}>
                      <strong style={{ fontSize: "15px", color: "var(--green-700, #2f7d62)" }}>
                        {rupiah.format(totalTHP)}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </table>

              {rows.length === 0 && (
                <p style={{ color: "#888", padding: "16px 0" }}>
                  Tidak ada nakes aktif untuk periode ini.
                </p>
              )}
            </>
          )}
        </article>
      )}

      <style>{`
        .pay-toolbar {
          display: flex;
          align-items: flex-end;
          gap: 20px;
          flex-wrap: wrap;
          margin-top: 8px;
        }
        .pay-period label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #6b6555;
          margin-bottom: 6px;
        }
        .pay-period-selects {
          display: flex;
          gap: 10px;
        }
        .pay-period-selects select {
          padding: 9px 12px;
          border: 1px solid #e8e2d5;
          border-radius: 8px;
          font-size: 14px;
          background: #fff;
        }
        .pay-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .pay-saved-badge {
          margin-top: 12px;
          padding: 8px 14px;
          background: #edfaf3;
          border: 1px solid #b2e8cc;
          border-radius: 8px;
          color: #1a6b41;
          font-size: 13px;
          font-weight: 600;
          width: fit-content;
        }
        .pay-detail-button {
          align-items: center;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 999px;
          color: #166534;
          cursor: pointer;
          display: inline-flex;
          font-size: 12px;
          font-weight: 800;
          gap: 4px;
          padding: 6px 10px;
        }
        .pay-detail-row td {
          background: #fbfff7;
          padding: 0 12px 14px;
        }
        .pay-report {
          border: 1px solid #d8f3df;
          border-radius: 10px;
          padding: 16px;
        }
        .pay-report-head {
          align-items: center;
          display: flex;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }
        .pay-report-head h3 {
          margin: 3px 0 0;
        }
        .pay-report-head .button {
          align-items: center;
          display: inline-flex;
          gap: 6px;
        }
        .pay-report-meta {
          border-bottom: 1px solid #cbd5e1;
          border-top: 3px solid #2563eb;
          display: grid;
          gap: 16px;
          grid-template-columns: 1fr 1fr 1fr;
          padding: 9px 0;
        }
        .pay-report-meta strong {
          display: block;
          margin-top: 4px;
        }
        .pay-report > h4 {
          color: #143326;
          font-size: 14px;
          margin: 17px 0 8px;
        }
        .pay-report-table {
          border-collapse: collapse;
          font-size: 13px;
          width: 100%;
        }
        .pay-report-table th {
          background: #eff6ff;
          border-bottom: 1px solid #cbd5e1;
          color: #142033;
          padding: 8px;
          text-align: left;
        }
        .pay-report-table td {
          border-bottom: 1px solid #dbe3ee;
          padding: 8px;
        }
        .pay-report-empty,
        .pay-report-loading {
          color: #64748b;
          padding: 14px !important;
          text-align: center;
        }
        .pay-report-totals {
          border-top: 3px solid #2563eb;
          margin-left: auto;
          margin-top: 12px;
          max-width: 560px;
          padding-top: 8px;
        }
        .pay-report-totals > div {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          padding: 4px 0;
        }
        .pay-report-grand {
          border-top: 1px solid #94a3b8;
          font-size: 16px;
          margin-top: 8px;
          padding-top: 9px !important;
        }
        .pay-detail-grid {
          border: 1px solid #d8f3df;
          border-radius: 10px;
          display: grid;
          gap: 14px;
          grid-template-columns: 1fr 1fr 1fr;
          padding: 14px;
        }
        .pay-detail-grid h4 {
          color: #143326;
          font-size: 13px;
          margin: 0 0 8px;
        }
        .pay-detail-grid p,
        .pay-detail-grid ul {
          color: #4b5f55;
          font-size: 13px;
          line-height: 1.55;
          margin: 0;
        }
        .pay-detail-grid ul {
          max-height: 160px;
          overflow: auto;
          padding-left: 18px;
        }
        .mt-table tfoot td {
          padding: 12px;
          font-size: 14px;
        }
        .mt-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .mt-table th {
          text-align: left; font-size: 11px; text-transform: uppercase;
          letter-spacing: 0.04em; color: #7a7364; padding: 10px 12px;
          border-bottom: 1px solid #e8e2d5;
        }
        .mt-table td { padding: 12px; border-bottom: 1px solid #f1ece0; }
        @media (max-width: 900px) {
          .pay-detail-grid { grid-template-columns: 1fr; }
          .pay-report-meta { grid-template-columns: 1fr; gap: 8px; }
          .pay-report { overflow-x: auto; }
          .pay-report-table { min-width: 680px; }
        }
      `}</style>
    </div>
  );
}
